// Regras puras do editor de marca d'agua (sem React, sem rede): mover e
// redimensionar camadas em fracoes da tela, validar o logo, comparar design
// salvo x em edicao e traduzir erros das RPCs.

import {
  MIN_LAYER_SIZE,
  WATERMARK_LOGO_MAX_BYTES,
  WATERMARK_LOGO_MIME_TYPES,
  normalizeBox,
  normalizeWatermarkDesign,
  type NormBox,
  type WatermarkDesign,
  type WatermarkFlow,
  type WatermarkLayer,
} from "@lucralink/shared";

export const FLOW_LABELS: Readonly<Record<WatermarkFlow, { label: string; hint: string }>> = {
  forwarding: { label: "Encaminhamento", hint: "Ofertas repostadas dos grupos que você monitora." },
  auto_promotions: {
    label: "Curadoria automática",
    hint: "Promoções escolhidas pelo LucraLink por categoria.",
  },
  scheduled_posts: {
    label: "Posts agendados",
    hint: "Disparos criados em Criar posts e no calendário.",
  },
  ai_images: { label: "Imagens de IA", hint: "Aplicada depois que a imagem é gerada." },
};

export const LAYER_LABELS: Readonly<Record<WatermarkLayer["kind"], string>> = {
  text: "Texto",
  badge: "Selo",
  contacts: "Contatos",
  logo: "Logo",
};

/** Move a caixa (deltas em fracao da tela), sempre dentro da tela. */
export function moveBox(box: NormBox, dx: number, dy: number): NormBox {
  return normalizeBox({ ...box, x: box.x + dx, y: box.y + dy }, box);
}

/** Redimensiona pelo canto inferior direito, com lado minimo e dentro da tela. */
export function resizeBox(box: NormBox, dw: number, dh: number): NormBox {
  if (!Number.isFinite(dw) || !Number.isFinite(dh)) return box;
  const w = Math.min(1 - box.x, Math.max(MIN_LAYER_SIZE, box.w + dw));
  const h = Math.min(1 - box.y, Math.max(MIN_LAYER_SIZE, box.h + dh));
  return { ...box, w, h };
}

/** Troca uma camada pelo id (imutavel). */
export function updateLayer(
  design: WatermarkDesign,
  id: string,
  patch: (layer: WatermarkLayer) => WatermarkLayer,
): WatermarkDesign {
  return {
    ...design,
    layers: design.layers.map((layer) => (layer.id === id ? patch(layer) : layer)),
  };
}

export function removeLayer(design: WatermarkDesign, id: string): WatermarkDesign {
  return { ...design, layers: design.layers.filter((layer) => layer.id !== id) };
}

/** Id livre para uma camada nova do tipo (logo, logo-2, ...). */
export function nextLayerId(design: WatermarkDesign, kind: WatermarkLayer["kind"]): string {
  const ids = new Set(design.layers.map((layer) => layer.id));
  if (!ids.has(kind)) return kind;
  let n = 2;
  while (ids.has(`${kind}-${n}`)) n++;
  return `${kind}-${n}`;
}

/** Design em edicao difere do salvo? (compara a forma normalizada). */
export function isDesignDirty(saved: WatermarkDesign, current: WatermarkDesign): boolean {
  return (
    JSON.stringify(normalizeWatermarkDesign(saved)) !==
    JSON.stringify(normalizeWatermarkDesign(current))
  );
}

const LOGO_EXTENSIONS: Readonly<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export type LogoCheck =
  | { readonly ok: true; readonly extension: string }
  | { readonly ok: false; readonly message: string };

/** PNG/JPEG/WebP ate 2 MB (mesma regra do bucket). */
export function checkLogoFile(file: { type: string; size: number }): LogoCheck {
  if (!(WATERMARK_LOGO_MIME_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, message: "Envie o logo em PNG, JPEG ou WebP." };
  }
  if (file.size === 0 || file.size > WATERMARK_LOGO_MAX_BYTES) {
    return { ok: false, message: "O logo deve ter no máximo 2 MB." };
  }
  return { ok: true, extension: LOGO_EXTENSIONS[file.type]! };
}

/** Caminho do logo no bucket: <tenant>/logo/<uuid>.<ext> (aceito pela RPC). */
export function logoPathFor(tenantId: string, uuid: string, extension: string): string {
  return `${tenantId}/logo/${uuid.toLowerCase()}.${extension}`;
}

const ERROR_MESSAGES: ReadonlyArray<[string, string]> = [
  ["watermark_requires_pro", "A marca d'água é do plano PRO."],
  ["watermark_preview_rate_limited", "Muitas prévias seguidas. Aguarde alguns segundos."],
  ["invalid_watermark_design", "O desenho da marca d'água está inválido. Recarregue a página."],
  ["invalid_watermark_flows", "Escolha onde aplicar a marca d'água."],
  ["invalid_watermark_logo_path", "Não foi possível usar este logo. Envie de novo."],
  ["watermark_logo_not_found", "O logo não terminou de subir. Envie de novo."],
];

export function watermarkErrorMessage(error: unknown): string {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error !== null && "message" in error
        ? String((error as { message: unknown }).message)
        : String(error);
  const match = ERROR_MESSAGES.find(([code]) => raw.includes(code));
  return match ? match[1] : "Não foi possível salvar agora. Tente de novo.";
}
